import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, FeedbackBanner, Panel, ReadinessMeter } from '@usacow/ds';
import { api } from '../../lib/api';

const SKILL_LABEL: Record<string, string> = {
  parsing: 'Parsing',
  modeling: 'Modeling',
  algorithm_choice: 'Algorithm choice',
  execution: 'Execution',
  proof: 'Proof',
  implementation: 'Implementation',
  retention: 'Retention',
};

type Data = Awaited<ReturnType<typeof api.coachStudent>>;

export function StudentProfile() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<Data | null>(null);

  useEffect(() => {
    api
      .coachStudent(id)
      .then(setData)
      .catch(() => navigate('/coach'));
  }, [id, navigate]);

  if (!data) return <div className="dim">Loading…</div>;
  const weakest = data.skills[0];

  return (
    <div className="scr scr--narrow">
      <button className="lib-back" onClick={() => navigate(-1)}>
        ← Back
      </button>
      <div className="scr-head">
        <div>
          <div className="t-eyebrow">student</div>
          <h1 className="scr-h">{data.student.name ?? data.student.email}</h1>
        </div>
        <Badge tone={data.readiness.band === 'ready' ? 'correct' : 'partial'}>
          {data.readiness.band.replace('_', ' ')}
        </Badge>
      </div>

      <FeedbackBanner state="warning" title="Bottleneck, not effort">
        Lead with where reasoning breaks down, not solved-count. This student&apos;s weakest skill
        is {weakest ? (SKILL_LABEL[weakest.skill] ?? weakest.skill) : '—'}.
      </FeedbackBanner>

      <Panel title="Readiness" style={{ marginTop: 14, marginBottom: 12 }}>
        <ReadinessMeter score={data.readiness.score} threshold={80} style={{ width: '100%' }} />
      </Panel>

      <Panel title="Reasoning skills">
        <div className="ms-skills">
          {data.skills.map((s) => {
            const v = s.score / 10;
            const color =
              v < 50 ? 'var(--incorrect)' : v < 68 ? 'var(--partial)' : 'var(--correct)';
            return (
              <div className="ms-skill" key={`${s.topic}-${s.skill}`}>
                <span className="ms-skill__n">{SKILL_LABEL[s.skill] ?? s.skill}</span>
                <span className="ms-skill__bar">
                  <span style={{ width: `${v}%`, background: color }} />
                </span>
                <span className="ms-skill__v">{Math.round(v)}</span>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}
